import { create } from 'zustand';
import { simulationService, SimulationBot, Simulation, SimulationMessage } from '@/services/simulation.service';

interface SimulationState {
  // Bots
  bots: SimulationBot[];
  botsLoading: boolean;
  fetchBots: () => Promise<void>;
  createBot: (data: Partial<SimulationBot>) => Promise<SimulationBot>;
  updateBot: (id: string, data: Partial<SimulationBot>) => Promise<void>;
  deleteBot: (id: string) => Promise<void>;

  // Simulations
  simulations: Simulation[];
  simulationsLoading: boolean;
  fetchSimulations: () => Promise<void>;
  startSimulation: (data: { bot_id: string; agent_id: string; config?: { delay_ms?: number; max_turns?: number; run_until_tasks_complete?: boolean } }) => Promise<Simulation>;
  advanceSimulation: (id: string) => Promise<Simulation>;
  stopSimulation: (id: string) => Promise<void>;

  // Active simulation view
  activeSimulation: Simulation | null;
  activeMessages: SimulationMessage[];
  messagesLoading: boolean;
  setActiveSimulation: (sim: Simulation | null) => void;
  fetchSimulation: (id: string) => Promise<void>;
  fetchMessages: (id: string) => Promise<void>;
}

export const useSimulationStore = create<SimulationState>((set) => ({
  // Bots
  bots: [],
  botsLoading: false,

  fetchBots: async () => {
    set({ botsLoading: true });
    try {
      const bots = await simulationService.getBots();
      set({ bots, botsLoading: false });
    } catch {
      set({ botsLoading: false });
      throw new Error('Failed to fetch bots');
    }
  },

  createBot: async (data) => {
    const bot = await simulationService.createBot(data);
    set((state) => ({ bots: [bot, ...state.bots] }));
    return bot;
  },

  updateBot: async (id, data) => {
    const updated = await simulationService.updateBot(id, data);
    set((state) => ({
      bots: state.bots.map((b) => (b.id === id ? updated : b)),
    }));
  },

  deleteBot: async (id) => {
    await simulationService.deleteBot(id);
    set((state) => ({
      bots: state.bots.filter((b) => b.id !== id),
    }));
  },

  // Simulations
  simulations: [],
  simulationsLoading: false,

  fetchSimulations: async () => {
    set({ simulationsLoading: true });
    try {
      const simulations = await simulationService.getSimulations();
      set({ simulations, simulationsLoading: false });
    } catch {
      set({ simulationsLoading: false });
      throw new Error('Failed to fetch simulations');
    }
  },

  startSimulation: async (data) => {
    const sim = await simulationService.startSimulation(data);
    set((state) => ({ simulations: [sim, ...state.simulations] }));
    return sim;
  },

  advanceSimulation: async (id) => {
    const updated = await simulationService.advanceSimulation(id);
    set((state) => ({
      simulations: state.simulations.map((s) => (s.id === id ? updated : s)),
      activeSimulation: state.activeSimulation?.id === id ? updated : state.activeSimulation,
    }));
    return updated;
  },

  stopSimulation: async (id) => {
    const updated = await simulationService.stopSimulation(id);
    set((state) => ({
      simulations: state.simulations.map((s) => (s.id === id ? updated : s)),
      activeSimulation: state.activeSimulation?.id === id ? updated : state.activeSimulation,
    }));
  },

  // Active simulation view
  activeSimulation: null,
  activeMessages: [],
  messagesLoading: false,

  setActiveSimulation: (sim) => set({ activeSimulation: sim, activeMessages: [] }),

  fetchSimulation: async (id) => {
    const sim = await simulationService.getSimulation(id);
    set({ activeSimulation: sim });
  },

  fetchMessages: async (id) => {
    set({ messagesLoading: true });
    try {
      const messages = await simulationService.getSimulationMessages(id);
      set({ activeMessages: messages, messagesLoading: false });
    } catch {
      set({ messagesLoading: false });
    }
  },
}));
